import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Activity, Fingerprint } from "lucide-react";

const HERO_IMAGE =
  "https://customer-assets-7cd3h4nn.emergentagent.net/job_secure-family-files-1/artifacts/tf7c38ld_c0f4606e6915e966c56f418e2b5c79f43b0d56311ccb2864631bbc616a13378b.png";

// Real product photograph — hands holding the iPhone with the BeParent app.
// Treated as a spotlight/clipped frame with subtle parallax and cyan halo.
export default function HeroProductShot() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], [40, -60]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.02, 0.98]);

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 60 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1], delay: 0.3 }}
      className="relative mx-auto w-[75%] md:w-full max-w-[560px]"
      style={{ perspective: 1400 }}
    >
      {/* Cyan halo glow */}
      <div
        aria-hidden
        className="absolute -inset-16 md:-inset-24 pointer-events-none"
        style={{
          background:
            "radial-gradient(50% 45% at 50% 55%, rgba(0,250,248,0.28), rgba(0,250,248,0.08) 40%, transparent 70%)",
          filter: "blur(30px)",
        }}
      />

      {/* Soft rim light */}
      <div
        aria-hidden
        className="absolute -inset-8 rounded-[48px] pointer-events-none"
        style={{
          boxShadow:
            "0 0 120px -20px rgba(0,250,248,0.28), inset 0 0 60px rgba(0,250,248,0.05)",
        }}
      />

      {/* Grain overlay */}
      <div className="absolute inset-0 bp-noise rounded-[40px]" aria-hidden />

      {/* Image frame — clipped, spotlighted */}
      <motion.div
        style={{ y, scale }}
        className="relative rounded-[36px] overflow-hidden"
      >
        {/* Grid backdrop inside frame */}
        <div
          aria-hidden
          className="absolute inset-0 opacity-30 mix-blend-screen"
          style={{
            background:
              "radial-gradient(60% 40% at 50% 50%, rgba(0,250,248,0.10), transparent 60%)",
          }}
        />

        <motion.img
          src={HERO_IMAGE}
          alt="Hände halten ein iPhone mit der BeParent App"
          loading="eager"
          decoding="async"
          className="relative block w-full h-auto select-none pointer-events-none"
          initial={{ scale: 1.08 }}
          animate={{ scale: 1 }}
          transition={{ duration: 2.4, ease: [0.22, 1, 0.36, 1] }}
          style={{
            filter:
              "brightness(0.96) contrast(1.06) saturate(0.9)",
          }}
        />

        {/* Cyan light-leak vignette on top-right */}
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(40% 30% at 80% 15%, rgba(0,250,248,0.18), transparent 60%), radial-gradient(60% 40% at 20% 85%, rgba(15,23,42,0.7), transparent 70%)",
          }}
        />

        {/* Dark bottom fade to blend into page */}
        <div
          aria-hidden
          className="absolute inset-x-0 bottom-0 h-24 pointer-events-none"
          style={{
            background:
              "linear-gradient(to bottom, transparent, rgba(15,23,42,0.9) 90%)",
          }}
        />

        {/* Subtle frame border */}
        <div
          aria-hidden
          className="absolute inset-0 rounded-[36px] pointer-events-none"
          style={{
            boxShadow:
              "inset 0 0 0 1px rgba(255,255,255,0.06), inset 0 0 40px rgba(0,0,0,0.35)",
          }}
        />
      </motion.div>

      {/* Floating meta chips */}
      <motion.div
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 1.4, duration: 0.8 }}
        className="hidden md:flex absolute -right-4 top-14 items-center gap-2 rounded-full border border-white/10 bg-[#0F172A]/85 backdrop-blur-xl px-3 py-1.5 shadow-[0_10px_30px_-10px_rgba(0,250,248,0.4)]"
      >
        <Fingerprint size={12} className="text-[#00FAF8]" />
        <span className="text-[11px] font-mono-bp text-white/85 whitespace-nowrap">
          Primär lokal
        </span>
      </motion.div>
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 1.6, duration: 0.8 }}
        className="hidden md:flex absolute -left-4 bottom-24 items-center gap-2 rounded-full border border-white/10 bg-[#0F172A]/85 backdrop-blur-xl px-3 py-1.5 shadow-[0_10px_30px_-10px_rgba(0,250,248,0.4)]"
      >
        <Activity size={12} className="text-[#00FAF8]" />
        <span className="text-[11px] font-mono-bp text-white/85 whitespace-nowrap">
          Musteranalyse aktiv
        </span>
      </motion.div>

      {/* Cyan corner marker (editorial crop hint) */}
      <div
        aria-hidden
        className="absolute -top-2 -left-2 w-10 h-10 pointer-events-none"
        style={{
          borderTop: "1px solid #00FAF8",
          borderLeft: "1px solid #00FAF8",
          borderTopLeftRadius: "8px",
          boxShadow: "0 0 20px rgba(0,250,248,0.4)",
        }}
      />
      <div
        aria-hidden
        className="absolute -bottom-2 -right-2 w-10 h-10 pointer-events-none"
        style={{
          borderBottom: "1px solid #00FAF8",
          borderRight: "1px solid #00FAF8",
          borderBottomRightRadius: "8px",
          boxShadow: "0 0 20px rgba(0,250,248,0.4)",
        }}
      />
    </motion.div>
  );
}
